import { useEffect, useState } from 'react';
import { adicionarContaCorrente, listarAgencias, listarBancos } from 'api/capitalFlow';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';

const initialForm = {
  CODBANCO: '',
  CODAGENCIABANCO: '',
  CODCC: '',
  DIGCC: ''
};

const useModalCadastroContaHook = ({ open, onClose, buscarContasCorrentes }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState(initialForm);
  const [lstBancos, setLstBancos] = useState([]);
  const [lstAgencias, setLstAgencias] = useState([]);
  const [loadingBancos, setLoadingBancos] = useState(false);
  const [loadingAgencias, setLoadingAgencias] = useState(false);
  const [loadingBtnCadastrar, setLoadingBtnCadastrar] = useState(false);

  useEffect(() => {
    if (open) {
      carregarBancos();
    }
  }, [open]);

  useEffect(() => {
    setForm(initialForm);
    setErrors(initialForm);
  }, [onClose]);

  const carregarBancos = async () => {
    setLoadingBancos(true);
    try {
      const res = await listarBancos();
      setLstBancos(res.data?.data ?? []);
    } catch (err) {
      openSnackbarMain('Ocorreu um erro ao carregar os bancos', 'error');
      console.error(err);
    } finally {
      setLoadingBancos(false);
    }
  };

  const carregarAgencias = async codBanco => {
    setLoadingAgencias(true);
    try {
      const res = await listarAgencias(codBanco);
      setLstAgencias(res.data?.data ?? []);
    } catch (err) {
      openSnackbarMain('Ocorreu um erro ao carregar as agências', 'error');
      console.error(err);
    } finally {
      setLoadingAgencias(false);
    }
  };

  const handleBancoChange = (_, banco) => {
    setForm(prev => ({
      ...prev,
      CODBANCO: banco?.CODBANCO || '',
      CODAGENCIABANCO: ''
    }));
    setLstAgencias([]);
    setErrors(prev => ({ ...prev, CODBANCO: '' }));

    if (banco) {
      carregarAgencias(banco.CODBANCO);
    }
  };

  const handleAgenciaChange = (_, agencia) => {
    setForm(prev => ({
      ...prev,
      CODAGENCIABANCO: agencia?.CODAGENCIABANCO || ''
    }));
    setErrors(prev => ({ ...prev, CODAGENCIABANCO: '' }));
  };

  const handleContaChange = e => {
    setForm(prev => ({ ...prev, CODCC: e.target.value }));
    setErrors(prev => ({ ...prev, CODCC: '' }));
  };

  const handleDigContaChange = e => {
    setForm(prev => ({ ...prev, DIGCC: e.target.value }));
    setErrors(prev => ({ ...prev, DIGCC: '' }));
  };

  const handleSubmit = () => {
    const newErrors = {};

    if (!form.CODBANCO) newErrors.CODBANCO = 'Banco obrigatório';
    if (!form.CODAGENCIABANCO) newErrors.CODAGENCIABANCO = 'Agência obrigatória';
    if (!form.CODCC) newErrors.CODCC = 'Conta obrigatória';
    if (!form.DIGCC) newErrors.DIGCC = 'Dígito obrigatório';

    if (Object.keys(newErrors).length) {
      setErrors(prev => ({ ...prev, ...newErrors }));
      return;
    }

    const agencia = lstAgencias.find(a => a.CODAGENCIABANCO === form.CODAGENCIABANCO);

    const params = {
      codbanco: form.CODBANCO,
      codagenciabanco: form.CODAGENCIABANCO,
      digagencia: agencia?.DIGAGENCIA || '',
      codcc: form.CODCC,
      digcc: form.DIGCC
    };

    setLoadingBtnCadastrar(true);

    adicionarContaCorrente(params)
      .then(() => {
        buscarContasCorrentes();
        setForm(initialForm);
        setLstAgencias([]);
        onClose();
      })
      .catch(err => {
        const data = err?.response?.data;
        const mensagem = data?.errors?.codcc?.[0] || data?.message || '';
        openSnackbarMain(`Ocorreu um erro ao adicionar a conta corrente: ${mensagem}`, 'error');
        console.error(err);
      })
      .finally(() => {
        setLoadingBtnCadastrar(false);
      });
  };

  const bancoValue = lstBancos.find(b => String(b.CODBANCO) === String(form.CODBANCO)) || null;

  const agenciaValue =
    lstAgencias.find(a => String(a.CODAGENCIABANCO) === String(form.CODAGENCIABANCO)) || null;

  return {
    form,
    errors,
    lstBancos,
    bancoValue,
    lstAgencias,
    agenciaValue,
    handleSubmit,
    loadingBancos,
    loadingAgencias,
    handleBancoChange,
    handleContaChange,
    loadingBtnCadastrar,
    handleAgenciaChange,
    handleDigContaChange
  };
};

export default useModalCadastroContaHook;
